import { z } from 'zod';
import { LogoDataSchema } from '../../shared/logo.schema';
import { SupportedLanguageSchema } from '../../shared/language.schema';
import { CapacityPrioritySchema } from '../../shared/capacity-priority';
import { InstalledAppSchema } from './installed-app.schema';
/**
 * Company Size Type Enum
 */ export const CompanySizeTypeSchema = z.enum([
    'micro',
    'small',
    'medium',
    'large'
]);
/**
 * License Model Type Enum
 */ export const LicenseModelTypeSchema = z.enum([
    'freemium',
    'premium',
    'enterprise'
]);
/**
 * Address Schema
 */ export const AddressSchema = z.object({
    country: z.string().optional(),
    city: z.string().optional(),
    zipCode: z.string().optional(),
    street: z.string().optional()
});
/**
 * License Schema
 */ export const LicenseSchema = z.object({
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    manualStatus: z.boolean().optional(),
    model: z.object({
        type: LicenseModelTypeSchema,
        size: z.object({
            min: z.number().optional(),
            max: z.number().optional()
        })
    })
});
/**
 * Company Size Schema
 */ export const CompanySizeSchema = z.object({
    type: CompanySizeTypeSchema,
    employeeCount: z.number(),
    turnover: z.number(),
    balanceSheetTotal: z.number()
});
/**
 * AI Model Enum
 */ export const AiModelSchema = z.enum([
    'gpt-6.1-sol',
    'gpt-6-sol',
    'gpt-6-luna',
    'claude-opus-5-5'
]);
/**
 * AI Reasoning Effort Enum, lowest first. Each model accepts its own
 * contiguous slice (`AI_MODEL_CONFIGS[model].reasoningEfforts`).
 */ export const AiReasoningEffortSchema = z.enum([
    'none',
    'low',
    'medium',
    'high',
    'xhigh',
    'max'
]);
/**
 * Transcription Model Enum
 */ export const TranscriptionModelSchema = z.enum([
    'gpt-4o-transcribe'
]);
/**
 * AI Settings Schema
 * Per-organization AI feature toggles (model selection is global via PlatformSettings)
 */ export const AiSettingsSchema = z.object({
    enableBliksy: z.boolean().optional(),
    enableStepNameGeneration: z.boolean().optional()
});
/**
 * Privacy Mode Enum
 * Controls entity visibility for internal users
 */ export const PrivacyModeSchema = z.enum([
    'full-transparency',
    'need-to-know'
]);
/**
 * Settings Schema
 * Nested organization settings
 */ export const SettingsSchema = z.object({
    enableRecordingSurvey: z.boolean().optional(),
    enableFrequencySurvey: z.boolean().optional(),
    enableDurationSurvey: z.boolean().optional(),
    enableVariantCheck: z.boolean().optional(),
    enableAI: z.boolean().optional(),
    reviewTimeframe: z.number().optional(),
    defaultLanguage: SupportedLanguageSchema.optional(),
    standardRecordingTime: z.number().optional(),
    ai: AiSettingsSchema.optional(),
    internalBillingRate: z.number().positive().optional(),
    privacyMode: PrivacyModeSchema.optional(),
    capacityPriority: CapacityPrioritySchema.optional()
});
/**
 * Initialization Schema
 * Tracks initialization status with timestamps
 */ export const InitializationSchema = z.object({
    departments: z.number().optional(),
    users: z.number().optional(),
    processes: z.number().optional(),
    orgFunctions: z.number().optional(),
    teams: z.number().optional(),
    systems: z.number().optional(),
    /**
   * Wizard finished (Abschließen). Per-step timestamps mean "data imported
   * at least once"; a step that is skipped stays unset — skip is derived
   * (completedAt set, step timestamp unset), never stored.
   */ completedAt: z.number().optional()
});
/**
 * Organization Schema
 * Represents an organization entity in the system
 */ export const OrganizationSchema = z.object({
    _id: z.string().optional(),
    image: z.string().optional(),
    logo: LogoDataSchema.optional(),
    active: z.boolean().optional(),
    name: z.string().optional(),
    slug: z.string().optional(),
    legalStructure: z.string().optional(),
    address: AddressSchema.optional(),
    website: z.string().optional(),
    license: LicenseSchema.optional(),
    businessModel: z.string().optional(),
    targetMarket: z.string().optional(),
    industryClassification: z.string().optional(),
    companySize: CompanySizeSchema.optional(),
    updatedAt: z.string().optional(),
    customerId: z.string().optional(),
    settings: SettingsSchema.optional(),
    initialization: InitializationSchema.optional(),
    apps: z.array(InstalledAppSchema).optional()
});

//# sourceMappingURL=organization.schema.js.map